import React from 'react';
import { Box, Container, Link, Typography } from '@mui/material';

const EFFECTIVE_DATE = 'April 22, 2026';

export const Privacy: React.FC = () => {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 5, md: 8 } }}>
      <Container maxWidth="md">
        <Typography variant="h6" component="a" href="/" sx={{ textDecoration: 'none', color: 'text.primary' }}>
          Chargaux
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mt: 4, mb: 1, fontWeight: 600 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Effective date: {EFFECTIVE_DATE}
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          This Privacy Policy describes how WDC Technology, LLC ("Chargaux," "we," "us") handles
          information in connection with the Chargaux service (the "Service"). Chargaux is a
          business-to-business platform provided to automotive dealership groups. We do not market
          the Service to individual consumers. Chargaux operates as a service provider and
          processor on behalf of our dealership customers, who are the controllers of the data
          processed through the Service.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>1. Information We Process</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          On behalf of our dealership customers, we process repair-order, parts, vehicle, and
          related operational data made available to us through the dealer's Dealer Management
          System (DMS) or other authorized source. This data may include limited personal
          information such as customer name, contact details, and vehicle identifiers.
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          We also process limited information about authorized users of the Service, such as name,
          email address, and authentication credentials.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>2. How We Use Information</Typography>
        <Typography variant="body1" color="text.secondary" component="ul" sx={{ pl: 3 }}>
          <li>To provide, maintain, and improve the Service for the dealership customer.</li>
          <li>To generate reports, analytics, and operational insights requested by the customer.</li>
          <li>To secure the Service, prevent fraud or abuse, and comply with legal obligations.</li>
          <li>To communicate service updates, support responses, and administrative messages.</li>
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mt: 2 }}>
          We do not sell personal information, and we do not use customer data to train
          third-party machine-learning models.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>3. Data Sharing and Sub-Processors</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          We share information only with service providers that help us operate the Service under
          appropriate contractual protections. Categories include cloud hosting, database and
          authentication infrastructure, transactional email delivery, and DMS connectivity
          providers. We may also disclose information when required by law or to protect the
          rights, property, or safety of Chargaux, our customers, or others.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>4. Security</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          We maintain administrative, technical, and physical safeguards designed to protect
          customer data, including encryption in transit and at rest, tenant-level isolation,
          role-based access controls, and audit logging. Our program is designed to support
          customer compliance obligations, including the Gramm-Leach-Bliley Act (GLBA) Safeguards
          Rule as it applies to dealership financial services activities. Upon confirmed
          unauthorized access to or disclosure of customer data, we will notify affected customers
          and applicable partners without undue delay, consistent with contractual and legal
          requirements. No method of transmission or storage is 100% secure; we work continuously
          to strengthen our controls.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>5. Data Retention</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          We retain customer data for as long as the customer's account is active, or as needed to
          provide the Service. Diagnostic and API transaction logs are retained for a limited
          period consistent with operational and compliance needs. Upon termination, we will delete
          or return customer data in accordance with the applicable customer agreement.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>6. Your Choices</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Because Chargaux acts as a processor on behalf of the dealership (the controller),
          requests to access, correct, or delete personal information should generally be directed
          to the dealership that supplied the data. Upon receiving a verifiable request, we will
          cooperate with the dealership controller to respond as required by applicable law. If
          you contact us directly, we will route your request to the appropriate dealership
          customer and assist them in responding.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>7. International Users</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          The Service is operated from the United States. If you access the Service from outside
          the United States, you understand that your information will be processed in the
          United States.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>8. Changes to This Policy</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          We may update this Privacy Policy from time to time. Material changes will be
          communicated through the Service or by other reasonable means. The "Effective date"
          above indicates when this policy was last revised.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>9. Contact Us</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Questions about this Privacy Policy or our data practices can be sent to{' '}
          <Link href="mailto:privacy@chargauxtech.com">privacy@chargauxtech.com</Link>.
        </Typography>

        <Box sx={{ mt: 8, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            &copy; 2026 WDC Technology, LLC. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Privacy;
